import React from 'react';
import moment from 'moment';
import { Box, Grid, Typography, Card } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LaborHours from './LaborHours';
import { InformationAlert } from '../../Common';
import { TranslateW } from '../../../Utils/Languages/Translate';
import { trakrefFontFamily } from '../../../utils';
import {
    trakrefBlue,
    Grey1,
    Grey3,
    Grey6,
    red,
} from '../../../color_utils';

// ---------------------------------------------------------------------------
// BASE UI for the Completion review page (ADO #14716).
// Renders one read-only summary card per visited workflow step in a two-column
// grid, with INCOMPLETE badges + "Action Required" indicators driven by the
// live required-field list. Labor Hours / GL Code stay editable at the bottom.
//
// NOTE: this is the base scaffold. The detailed field mapping for every SE
// type, the Gas Transaction Recovery/Install sub-section tables, and the
// "Submit Work Order" disable wiring are intentionally left as follow-ups.
// Add/refine per-step fields in CARD_CONFIG below.
// ---------------------------------------------------------------------------

// Options are shaped { id, name }. Resolve an id to its display label.
const labelFor = (options = [], id) => {
    if (id === null || id === undefined || id === '') return '';
    const match = (options || []).find(o => o.id === id || o.value === id);
    return match ? (match.name ?? match.label ?? '') : '';
};

const yesNo = v => (v === true ? 'Yes' : v === false ? 'No' : '');

const formatDate = d => (d && moment(d).isValid() ? moment(d).format('MM/DD/YYYY') : '');

const isEmptyValue = v => v === '' || v === null || v === undefined;

// Humanize a bare formState key (e.g. "leakLocationId" -> "Leak Location") for any
// required field we haven't explicitly mapped in CARD_CONFIG, so it still shows up.
const humanizeKey = key => key
    .replace(/Id$/, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();

// Per-step card configuration. `requiredKey` matches the bare field name used in
// serviceEventTypesRequiredFields / requiredFieldsRef so we can flag Action Required.
const CARD_CONFIG = {
    RepairDetails: {
        title: 'Discovery',
        // validateRepairDetails reports missing fields nested under this key.
        nestedKey: 'initialLeakInspection',
        fields: [
            { label: 'Leak Detection Method', requiredKey: 'leakDetectionMethodId', get: (fs, opts) => labelFor(opts.leakDetectionMethods, fs.initialLeakInspection?.leakDetectionMethodId) },
            { label: 'Leak Found', requiredKey: 'wasLeakFound', get: fs => yesNo(fs.initialLeakInspection?.wasLeakFound) },
            { label: 'Date Leak Found', requiredKey: 'inspectionDate', get: fs => formatDate(fs.initialLeakInspection?.inspectionDate) },
            { label: 'Leak Location', requiredKey: 'leakLocationId', get: (fs, opts) => labelFor(opts.leakLocations, fs.initialLeakInspection?.leakLocationId) },
            { label: 'Cause of Leak', requiredKey: 'faultCauseTypeId', get: (fs, opts) => labelFor(opts.leakCauses, fs.initialLeakInspection?.faultCauseTypeId) },
        ],
    },
    RepairNotes: {
        title: 'Repair Details',
        fields: [
            { label: 'Repair Status', requiredKey: 'repairOutcomeTypeId', get: (fs, opts) => labelFor(opts.repairOutcomeTypes, fs.repairOutcomeTypeId) },
            { label: 'Service Action', requiredKey: 'serviceActionId', get: (fs, opts) => labelFor(opts.serviceActions, fs.serviceActionId) },
            { label: 'Repair Notes', requiredKey: 'repairNotes', get: fs => fs.repairNotes },
            { label: 'Parts Required', get: fs => fs.partsRequired },
        ],
    },
    InitialVerificationTest: {
        title: 'Verification',
        nestedKey: 'initialVerificationLeakInspection',
        fields: [
            { label: 'Verification Date', requiredKey: 'inspectionDate', get: fs => formatDate(fs.initialVerificationLeakInspection?.inspectionDate) },
            { label: 'Detection Method', requiredKey: 'leakDetectionMethodId', get: (fs, opts) => labelFor(opts.leakDetectionMethods, fs.initialVerificationLeakInspection?.leakDetectionMethodId) },
            { label: 'Leak Found', requiredKey: 'wasLeakFound', get: fs => yesNo(fs.initialVerificationLeakInspection?.wasLeakFound) },
        ],
    },
    GasTransaction: {
        title: 'Gas Transaction',
        // TODO: replace with Recovery/Install sub-section tables (TransferTable).
        fields: [
            { label: 'Recovery Entries', get: fs => (fs.recoveries?.length ? String(fs.recoveries.length) : '') },
            { label: 'Install Entries', get: fs => (fs.installs?.length ? String(fs.installs.length) : '') },
        ],
    },
    FollowUpVerificationService: {
        title: 'Follow Up Verification',
        nestedKey: 'verificationLeakInspection',
        fields: [
            { label: 'Follow Up Date', requiredKey: 'inspectionDate', get: fs => formatDate(fs.verificationLeakInspection?.inspectionDate) },
            { label: 'Detection Method', requiredKey: 'leakDetectionMethodId', get: (fs, opts) => labelFor(opts.leakDetectionMethods, fs.verificationLeakInspection?.leakDetectionMethodId) },
            { label: 'Leak Found', requiredKey: 'wasLeakFound', get: fs => yesNo(fs.verificationLeakInspection?.wasLeakFound) },
            { label: 'Leak Location', requiredKey: 'leakLocationId', get: (fs, opts) => labelFor(opts.leakLocations, fs.verificationLeakInspection?.leakLocationId) },
            { label: 'Cause of Leak', requiredKey: 'faultCauseTypeId', get: (fs, opts) => labelFor(opts.leakCauses, fs.verificationLeakInspection?.faultCauseTypeId) },
        ],
    },
};

const ActionRequiredValue = ({ onClick }) => (
    <Typography
        onClick={onClick}
        sx={{
            color: trakrefBlue,
            fontFamily: trakrefFontFamily,
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'underline',
            cursor: onClick ? 'pointer' : 'default',
        }}
    >
        Action Required
    </Typography>
);

// Required keys that are satisfied implicitly by another (mapped) field and so
// must NOT render their own row. leakLocationCategoryId is consolidated into the
// single Leak Location autocomplete (#14542), so it has no standalone UI field.
const SUPPRESSED_REQUIRED_KEYS = new Set(['leakLocationCategoryId']);

// validateAllSteps() returns a MIXED-shape missingFieldsObject: some steps report
// flat keys (e.g. RepairNotes -> { serviceActionId: true }) while others nest under
// a sub-object (e.g. RepairDetails -> { initialLeakInspection: { wasLeakFound: true } },
// IVT -> { initialVerificationLeakInspection: {...} }). Check the card's nested bucket
// first (so Discovery/Verification/Follow-Up flag correctly and don't collide on the
// shared bare keys), then fall back to a flat top-level key.
const isRequiredMissing = (missingFieldsObject, nestedKey, key) => {
    if (nestedKey) {
        const nested = missingFieldsObject[nestedKey];
        if (nested && typeof nested === 'object' && nested[key]) return true;
    }
    return Boolean(missingFieldsObject[key]);
};

// Evaluate a card's rows + completeness. "Action Required" is driven by the app's
// own validation (missingFieldsObject from validateAllSteps) so it respects
// conditional requirements (e.g. IVT not required when Repair Status = "No Repair
// Attempted"). The per-card value getter disambiguates fields that share a bare
// key across steps (e.g. leakDetectionMethodId on Discovery vs Verification).
const evaluateCard = (config, requiredKeys, formState, dropdownOptions, missingFieldsObject) => {
    const configuredRequiredKeys = new Set(config.fields.filter(f => f.requiredKey).map(f => f.requiredKey));

    // Show a row for any required field we haven't mapped — but only when the app's
    // validation actually reports it missing, so satisfied/consolidated keys stay hidden.
    const fallbackFields = requiredKeys
        .filter(key => !configuredRequiredKeys.has(key)
            && !SUPPRESSED_REQUIRED_KEYS.has(key)
            && isRequiredMissing(missingFieldsObject, config.nestedKey, key))
        .map(key => ({ label: humanizeKey(key), requiredKey: key, get: () => '' }));

    const rows = [...config.fields, ...fallbackFields].map(field => {
        const value = field.get ? field.get(formState, dropdownOptions) : '';
        const actionRequired = Boolean(field.requiredKey)
            && !SUPPRESSED_REQUIRED_KEYS.has(field.requiredKey)
            && isRequiredMissing(missingFieldsObject, config.nestedKey, field.requiredKey)
            && isEmptyValue(value);
        return { ...field, value, actionRequired };
    });

    return { rows, incomplete: rows.some(r => r.actionRequired) };
};

const StepCard = ({ config, stepId, formState, dropdownOptions, requiredKeys, missingFieldsObject, onCardClick }) => {
    const { rows, incomplete } = evaluateCard(config, requiredKeys, formState, dropdownOptions, missingFieldsObject);

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: Grey6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                }}
            >
                <Typography
                    sx={{
                        color: Grey1,
                        fontFamily: trakrefFontFamily,
                        fontSize: '12px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                    }}
                >
                    {config.title}
                </Typography>
                {incomplete && (
                    <Box sx={{ alignItems: 'center', color: red, display: 'flex', gap: '4px' }}>
                        <WarningAmberIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ color: red, fontFamily: trakrefFontFamily, fontSize: '12px', fontWeight: 700 }}>
                            INCOMPLETE
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box>
                {rows.map(field => (
                    <Box
                        key={field.label}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '12px',
                            padding: '10px 16px',
                        }}
                    >
                        <Typography
                            sx={{
                                color: field.actionRequired ? red : Grey3,
                                fontFamily: trakrefFontFamily,
                                fontSize: '13px',
                                fontWeight: field.actionRequired ? 600 : 400,
                            }}
                        >
                            {field.label}
                        </Typography>
                        {field.actionRequired ? (
                            <ActionRequiredValue onClick={onCardClick ? () => onCardClick(stepId) : null} />
                        ) : (
                            <Typography
                                sx={{
                                    color: Grey1,
                                    fontFamily: trakrefFontFamily,
                                    fontSize: '13px',
                                    textAlign: 'right',
                                }}
                            >
                                {isEmptyValue(field.value) ? '—' : field.value}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Card>
    );
};

const Completion = ({
    formState,
    setGlobalState,
    dropdownOptions,
    isEditing,
    isAdmin,
    serviceEventSteps = [],
    skippedStepIds = [],
    requiredFieldsRef,
    missingFieldsObject = {},
    onCardClick,
}) => {
    // Cards for every visited step except Enter Service (shown in the header) and
    // Completion itself. Skipped (conditionally absent) steps are excluded.
    const cardSteps = serviceEventSteps
        .map(s => s.props.id)
        .filter(id => id !== 'EnterService' && id !== 'Completion' && !skippedStepIds.includes(id));

    const requiredFor = stepId => (requiredFieldsRef?.current?.[stepId]) || [];

    const cards = cardSteps.map(stepId => {
        const config = CARD_CONFIG[stepId] || { title: stepId, fields: [] };
        return { stepId, config };
    });

    // Banner shows when any card has a genuinely-missing required field (per the app's
    // own validation), so it clears once everything required is filled.
    const anyIncomplete = cards.some(({ stepId, config }) =>
        evaluateCard(config, requiredFor(stepId), formState, dropdownOptions, missingFieldsObject).incomplete
    );

    return (
        <Box>
            <Box sx={{ marginBottom: 2 }}>
                <Typography sx={{ color: Grey1, fontFamily: trakrefFontFamily, fontSize: '16px', fontWeight: 600 }}>
                    {TranslateW('Completion') || 'Completion'}
                </Typography>
                <Typography sx={{ color: Grey3, fontFamily: trakrefFontFamily, fontSize: '13px' }}>
                    Review all entered data before submitting the work order.
                </Typography>
            </Box>

            {anyIncomplete && (
                <Box sx={{ marginBottom: 2 }}>
                    <InformationAlert
                        severity="error"
                        title="Action Required"
                        message={"Please review the cards below and complete all fields marked 'Action Required.'"}
                        onClose={null}
                    />
                </Box>
            )}

            <Grid container spacing={2}>
                {cards.map(({ stepId, config }) => (
                    <Grid item xs={12} md={6} key={stepId}>
                        <StepCard
                            config={config}
                            stepId={stepId}
                            formState={formState}
                            dropdownOptions={dropdownOptions}
                            requiredKeys={requiredFor(stepId)}
                            missingFieldsObject={missingFieldsObject}
                            onCardClick={onCardClick}
                        />
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ marginTop: 3, paddingTop: 3 }}>
                <LaborHours
                    formState={formState}
                    setGlobalState={setGlobalState}
                    dropdownOptions={dropdownOptions}
                    isEditing={isEditing}
                    isAdmin={isAdmin}
                />
            </Box>
        </Box>
    );
};

export default Completion;
